/**
 * MechanicFindingsList
 * ────────────────────
 * Row-based UI for structured mechanic findings on a job card.
 *
 * Each finding row stores:
 *   - text (what the mechanic found)
 *   - quantity
 *   - estimated_hours (rough labour estimate)
 *   - severity (low / medium / high / urgent)
 *   - photos[] (compressed JPEGs attached for SM review)
 *
 * The Service Manager later pulls these into the Quote Builder via the
 * "Get Findings" button (see ServiceNotesModal). Service Manager can also
 * view photo evidence via the "View images" menu next to each finding.
 */
import { useState, useRef, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Plus, Trash2, AlertCircle, CheckCircle2, Camera, MoreVertical, Image as ImageIcon, X, FileDown, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import SupplierCombobox from '@/components/invoices/SupplierCombobox';
import { AuthImage } from '@/components/common/AuthImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-rose-100 text-rose-700' },
];

export default function MechanicFindingsList({ jobCard, canEdit, isServiceManager = false, onUpdated }) {
  const allItems = jobCard?.findings_items || [];
  // A finding is "pending" when it hasn't been promoted onto the JC's
  // line_items as a task (i.e. SM hasn't yet quoted + customer hasn't
  // approved + SM hasn't pushed it to Job Card). Once it IS promoted,
  // the finding gets `added_to_tasks_at` stamped by
  // /invoices/{id}/add-approved-to-job-card and disappears from the
  // default list — the mechanic should be working off the Tasks section
  // for those items, not seeing them as "still to investigate".
  const pendingItems = useMemo(
    () => allItems.filter(f => !f.added_to_tasks_at),
    [allItems],
  );
  const promotedCount = allItems.length - pendingItems.length;
  // SM can flip a toggle to see the promoted-and-actioned findings for
  // audit. Defaults to false so the everyday view stays clean.
  const [showPromoted, setShowPromoted] = useState(false);
  const items = showPromoted ? allItems : pendingItems;
  const [draft, setDraft] = useState({ text: '', quantity: 1, estimated_hours: '', severity: 'medium', parts_supplier: '', parts_cost_price: '' });
  const [pendingPhotos, setPendingPhotos] = useState([]); // base64 strings to attach when row is created
  // iter138.5 — Index into pendingPhotos of the photo currently being
  // previewed full-size. Null = no preview open. Lets the mechanic tap a
  // thumbnail to verify focus before saving the finding.
  const [pendingPreviewIdx, setPendingPreviewIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewerFinding, setViewerFinding] = useState(null);
  const fileInputRef = useRef(null);
  const rowFileInputRef = useRef(null);
  const [rowFileTarget, setRowFileTarget] = useState(null); // finding_id currently uploading to
  const [pullingReport, setPullingReport] = useState(false);

  const refresh = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}`, { credentials: 'include' });
      if (res.ok) {
        const fresh = await res.json();
        onUpdated?.(fresh);
      }
    } catch (_e) { /* noop */ }
  };

  // iter188 — "Get Report": pull the quoted service notes / recommended
  // items from the customer's MOST RECENT invoice for THIS vehicle into
  // the findings, so the mechanic can re-check work that was previously
  // quoted-but-not-done. Carries a pricing snapshot forward for re-quoting.
  const getReport = async () => {
    setPullingReport(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/import-last-invoice-report`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not fetch report');
      }
      const d = await res.json();
      if (d.added > 0) {
        toast.success(d.message);
        await refresh();
      } else {
        toast.info(d.message || 'Nothing new to pull');
      }
    } catch (e) {
      toast.error(e.message || 'Could not fetch report');
    } finally {
      setPullingReport(false);
    }
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleDraftPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await readFileAsBase64(file);
      setPendingPhotos(prev => [...prev, { name: file.name, data: b64 }]);
    } catch (_e) {
      toast.error('Could not read photo');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRowPhoto = async (e) => {
    const file = e.target.files?.[0];
    const fid = rowFileTarget;
    if (!file || !fid) return;
    try {
      const b64 = await readFileAsBase64(file);
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/findings/${fid}/photos`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_data: b64, filename: file.name }),
      });
      if (!res.ok) {
        const er = await res.json().catch(() => ({}));
        throw new Error(er.detail || 'Photo upload failed');
      }
      toast.success('Photo attached');
      await refresh();
    } catch (er) {
      toast.error(er.message || 'Photo upload failed');
    } finally {
      setRowFileTarget(null);
      if (rowFileInputRef.current) rowFileInputRef.current.value = '';
    }
  };

  const uploadPhotosForFinding = async (findingId, photos) => {
    for (const p of photos) {
      try {
        await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/findings/${findingId}/photos`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_data: p.data, filename: p.name }),
        });
      } catch (_e) { /* keep going */ }
    }
  };

  const addFinding = async () => {
    const text = readFindingText();
    if (!text) {
      toast.error('Describe what you found');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/findings`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          quantity: Number(draft.quantity) || 1,
          estimated_hours: draft.estimated_hours === '' ? null : Number(draft.estimated_hours),
          severity: draft.severity || 'medium',
          // iter128 — SM-only. Backend will reject (or simply ignore) the
          // mechanic role's submission if they somehow craft a request.
          // The frontend hides the fields for non-SM roles.
          parts_supplier: (draft.parts_supplier || '').trim() || null,
          parts_cost_price: draft.parts_cost_price === '' ? null : Number(draft.parts_cost_price),
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Save failed');
      }
      const created = await res.json();
      if (pendingPhotos.length && created.finding_id) {
        await uploadPhotosForFinding(created.finding_id, pendingPhotos);
      }
      await refresh();
      setDraft({ text: '', quantity: 1, estimated_hours: '', severity: 'medium', parts_supplier: '', parts_cost_price: '' });
      clearFindingTextarea();
      setPendingPhotos([]);
      toast.success(`Finding added${pendingPhotos.length ? ` with ${pendingPhotos.length} photo(s)` : ''}`);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // iter138.9u — Uncontrolled textarea for finding text. Per-keystroke
  // setDraft was forcing the entire MechanicFindingsList (existing rows
  // + photo thumbnails + supplier comboboxes) to re-render — visible
  // typing lag on the mechanic's iPad. Ref read happens only on
  // Add / blur. State stays in sync via a low-key onChange that does
  // NOT trigger re-render (writes to ref + sets a single hidden tick
  // counter no consumer reads).
  const findingTextRef = useRef('');
  const findingTextareaRef = useRef(null);
  // iter138.9u — lightweight boolean to keep the [+] Add button
  // enabled/disabled in sync with the uncontrolled textarea. A boolean
  // state change only re-renders the MechanicFindingsList ONCE per
  // empty/non-empty transition (not every keystroke), so the perf
  // win from the uncontrolled textarea is preserved.
  const [hasFindingText, setHasFindingText] = useState(false);
  const readFindingText = () => (findingTextareaRef.current?.value ?? findingTextRef.current ?? '').trim().toUpperCase();
  const clearFindingTextarea = () => {
    findingTextRef.current = '';
    if (findingTextareaRef.current) findingTextareaRef.current.value = '';
    setHasFindingText(false);
  };

  // iter194 — inline edit mode for an existing finding. Same permission
  // rule as delete: assigned mechanic or SM/FM/SA, locked once promoted.
  const [editing, setEditing] = useState(null);
  const startEdit = (f) => setEditing({
    finding_id: f.finding_id,
    text: f.text || '',
    quantity: f.quantity ?? 1,
    estimated_hours: f.estimated_hours ?? '',
    severity: f.severity || 'medium',
    parts_supplier: f.parts_supplier || '',
    parts_cost_price: f.parts_cost_price ?? '',
  });

  const saveEdit = async () => {
    const text = (editing?.text || '').trim().toUpperCase();
    if (!text) {
      toast.error('Finding text cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const body = {
        text,
        quantity: Number(editing.quantity) || 1,
        estimated_hours: editing.estimated_hours === '' || editing.estimated_hours == null ? null : Number(editing.estimated_hours),
        severity: editing.severity || 'medium',
      };
      if (isServiceManager) {
        body.parts_supplier = (editing.parts_supplier || '').trim();
        body.parts_cost_price = editing.parts_cost_price === '' || editing.parts_cost_price == null ? null : Number(editing.parts_cost_price);
      }
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/findings/${editing.finding_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Update failed');
      }
      setEditing(null);
      await refresh();
      toast.success('Finding updated');
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteFinding = async (findingId) => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/findings/${findingId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const deletePhoto = async (findingId, photoId) => {
    if (!window.confirm('Remove this photo from the finding?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/findings/${findingId}/photos/${photoId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      await refresh();
      // Update local viewer state
      if (viewerFinding?.finding_id === findingId) {
        setViewerFinding(prev => ({ ...prev, photos: (prev.photos || []).filter(p => p.photo_id !== photoId) }));
      }
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-2" data-testid="mech-findings-list">
      {canEdit && (
        <div className="flex items-center justify-between gap-2 bg-blue-50/60 border border-blue-200 rounded px-2 py-1.5">
          <span className="text-[11px] text-blue-900">
            Returning customer? Pull what was quoted last visit for this vehicle.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-blue-300 text-blue-800 hover:bg-blue-100 flex-shrink-0"
            onClick={getReport}
            disabled={pullingReport}
            data-testid="findings-get-report-btn"
          >
            {pullingReport ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}
            Get Report
          </Button>
        </div>
      )}
      {promotedCount > 0 && (
        <div className="flex items-center justify-between text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
          <span data-testid="findings-promoted-summary">
            {promotedCount} finding{promotedCount === 1 ? '' : 's'} already approved &amp; moved to <strong>Tasks</strong>
          </span>
          <button
            type="button"
            onClick={() => setShowPromoted(v => !v)}
            className="text-blue-600 hover:underline"
            data-testid="findings-toggle-promoted"
          >
            {showPromoted ? 'hide actioned' : 'show actioned'}
          </button>
        </div>
      )}
      {items.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
          {items.map(f => {
            const sev = SEVERITY_OPTIONS.find(s => s.value === f.severity) || SEVERITY_OPTIONS[1];
            const photoCount = (f.photos || []).length;
            const isPromoted = !!f.added_to_tasks_at;
            if (editing?.finding_id === f.finding_id) {
              return (
                <li key={f.finding_id} className="p-2 bg-blue-50/60" data-testid={`finding-edit-row-${f.finding_id}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className={`col-span-12 ${isServiceManager ? 'md:col-span-4' : 'md:col-span-6'}`}>
                      <label className="text-[10px] uppercase tracking-wide text-blue-700">Edit finding</label>
                      <Textarea
                        value={editing.text}
                        onChange={(e) => setEditing(ed => ({ ...ed, text: e.target.value }))}
                        rows={2}
                        className="text-base bg-white touch-manipulation min-h-[64px] resize-y leading-snug uppercase"
                        data-testid="finding-edit-text"
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <label className="text-[10px] uppercase tracking-wide text-slate-500">Qty</label>
                      <Input
                        type="number" inputMode="decimal" min="0" step="0.5"
                        value={editing.quantity}
                        onChange={(e) => setEditing(ed => ({ ...ed, quantity: e.target.value }))}
                        className="h-10 text-sm text-right bg-white touch-manipulation"
                        data-testid="finding-edit-qty"
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <label className="text-[10px] uppercase tracking-wide text-slate-500">Hrs</label>
                      <Input
                        type="number" inputMode="decimal" min="0" step="0.25"
                        value={editing.estimated_hours}
                        onChange={(e) => setEditing(ed => ({ ...ed, estimated_hours: e.target.value }))}
                        placeholder="est"
                        className="h-10 text-sm text-right bg-white touch-manipulation"
                        data-testid="finding-edit-hours"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <label className="text-[10px] uppercase tracking-wide text-slate-500">Severity</label>
                      <Select value={editing.severity} onValueChange={(v) => setEditing(ed => ({ ...ed, severity: v }))}>
                        <SelectTrigger className="h-10 text-sm bg-white touch-manipulation" data-testid="finding-edit-severity">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITY_OPTIONS.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {isServiceManager && (
                      <>
                        <div className="col-span-8 md:col-span-1">
                          <label className="text-[10px] uppercase tracking-wide text-emerald-700">Supplier</label>
                          <SupplierCombobox
                            value={editing.parts_supplier || ''}
                            onChange={(v) => setEditing(ed => ({ ...ed, parts_supplier: v }))}
                            placeholder="Repco…"
                            className="bg-white"
                            testId="finding-edit-supplier"
                            size="md"
                          />
                        </div>
                        <div className="col-span-4 md:col-span-1">
                          <label className="text-[10px] uppercase tracking-wide text-emerald-700">ORP $</label>
                          <Input
                            type="number" inputMode="decimal" min="0" step="0.01"
                            value={editing.parts_cost_price}
                            onChange={(e) => setEditing(ed => ({ ...ed, parts_cost_price: e.target.value }))}
                            placeholder="cost"
                            className="h-10 text-sm text-right bg-white touch-manipulation"
                            data-testid="finding-edit-orp"
                          />
                        </div>
                      </>
                    )}
                    <div className="col-span-6 md:col-span-1">
                      <Button
                        size="sm"
                        onClick={saveEdit}
                        disabled={saving || !(editing.text || '').trim()}
                        className="h-10 w-full text-xs bg-blue-600 hover:bg-blue-700 touch-manipulation"
                        data-testid="finding-edit-save"
                      >
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    <div className="col-span-6 md:col-span-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(null)}
                        disabled={saving}
                        className="h-10 w-full text-xs touch-manipulation"
                        data-testid="finding-edit-cancel"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </li>
              );
            }
            return (
              <li
                key={f.finding_id}
                className={`flex items-start gap-2 p-2 hover:bg-slate-50 ${isPromoted ? 'opacity-60 bg-emerald-50/30' : ''}`}
                data-testid={`finding-row-${f.finding_id}`}
              >
                <AlertCircle className={`h-3.5 w-3.5 mt-1 flex-shrink-0 ${sev.color.split(' ')[1]}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">{f.text}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500 flex-wrap">
                    <span>qty {f.quantity || 1}</span>
                    {f.estimated_hours != null && (<><span>·</span><span>est {f.estimated_hours}h</span></>)}
                    <span className={`px-1.5 py-0.5 rounded-full ${sev.color}`}>{sev.label}</span>
                    {/* iter128 — SM-only supplier + ORP chips. Mechanic
                        users never see ORP, but the supplier hint is
                        only shown to SM/FM/SA to keep the row terse
                        for techs. */}
                    {isServiceManager && f.parts_supplier && (
                      <span
                        className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                        data-testid={`finding-supplier-${f.finding_id}`}
                      >
                        @{f.parts_supplier}
                      </span>
                    )}
                    {isServiceManager && f.parts_cost_price != null && (
                      <span
                        className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                        title="ORP — Our Real Price (supplier cost). Visible to SM / FM / SA only."
                        data-testid={`finding-orp-${f.finding_id}`}
                      >
                        ORP ${Number(f.parts_cost_price).toFixed(2)}
                      </span>
                    )}
                    {photoCount > 0 && (
                      <button
                        type="button"
                        className="flex items-center gap-0.5 text-blue-600 hover:underline"
                        onClick={() => setViewerFinding(f)}
                        data-testid={`finding-photo-count-${f.finding_id}`}
                      >
                        <ImageIcon className="h-3 w-3" />
                        {photoCount} photo{photoCount === 1 ? '' : 's'}
                      </button>
                    )}
                    {f.imported_to_quote_at && !isPromoted && (
                      <span className="text-emerald-600 flex items-center gap-0.5">
                        <CheckCircle2 className="h-3 w-3" />
                        imported to quote
                      </span>
                    )}
                    {isPromoted && (
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase bg-emerald-100 text-emerald-700 flex items-center gap-0.5">
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        moved to tasks
                      </span>
                    )}
                  </div>
                </div>
                {/* Inline camera shortcut for assigned mechanic */}
                {canEdit && !f.imported_to_quote_at && (
                  <button
                    type="button"
                    onClick={() => {
                      setRowFileTarget(f.finding_id);
                      // small timeout so target is set before the click triggers
                      setTimeout(() => rowFileInputRef.current?.click(), 0);
                    }}
                    className="text-slate-500 hover:text-blue-600 p-2 min-h-11 min-w-11 inline-flex items-center justify-center touch-manipulation"
                    title="Add photo to this finding"
                    disabled={saving}
                    data-testid={`finding-camera-${f.finding_id}`}
                  >
                    <Camera className="h-5 w-5" />
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-slate-800 p-2 min-h-11 min-w-11 inline-flex items-center justify-center touch-manipulation"
                      title="More"
                      data-testid={`finding-menu-${f.finding_id}`}
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setViewerFinding(f)}
                      disabled={photoCount === 0}
                      data-testid={`finding-view-images-${f.finding_id}`}
                    >
                      <ImageIcon className="h-3.5 w-3.5 mr-2" />
                      View image{photoCount === 1 ? '' : 's'} {photoCount > 0 ? `(${photoCount})` : ''}
                    </DropdownMenuItem>
                    {canEdit && !f.imported_to_quote_at && (
                      <DropdownMenuItem
                        onClick={() => startEdit(f)}
                        data-testid={`finding-edit-menu-${f.finding_id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-2" />
                        Edit finding
                      </DropdownMenuItem>
                    )}
                    {canEdit && !f.imported_to_quote_at && (
                      <DropdownMenuItem
                        onClick={() => deleteFinding(f.finding_id)}
                        className="text-rose-600"
                        data-testid={`finding-delete-menu-${f.finding_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        Remove finding
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            );
          })}
        </ul>
      )}

      {canEdit && (
        <div className="border border-slate-200 rounded p-2 bg-amber-50/40">
          {/* iter138.5 — SINGLE-ROW editor. Previously the SM-only Supplier
              + ORP fields sat in their own row underneath, which doubled
              the editor height and pushed the Tasks section off-screen
              on iPads. Now all 6/8 cells live on a single 12-col grid:
              Finding · Qty · Hrs · Severity · [Supplier · ORP if SM] ·
              Camera · ＋. Mobile still wraps via the col-span-12 base. */}
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className={`col-span-12 ${isServiceManager ? 'md:col-span-3' : 'md:col-span-5'}`}>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Add finding</label>
              {/* iter138.4 — Multi-line <Textarea> (was single-line Input).
                  On the mechanic's iPad, long findings used to overflow
                  the single-line input horizontally and the cursor
                  became impossible to position by tap. Textarea wraps
                  naturally + supports tap-anywhere caret placement.
                  Cmd/Ctrl+Enter still submits so iPad keyboard users
                  don't have to reach for the + button. */}
              {/* iter138.9u — UNCONTROLLED textarea. Previously
                  `value={draft.text}` + `onChange` setDraft caused the
                  entire MechanicFindingsList to re-render on every
                  keystroke, which was visibly laggy on the mechanic's
                  iPad once a JC had ≥3-4 findings rows with photo
                  thumbnails. We now write straight to a ref and only
                  read it on Add. Cmd/Ctrl+Enter still works because
                  the ref is read directly inside addFinding. */}
              <Textarea
                ref={findingTextareaRef}
                defaultValue={draft.text}
                onChange={(e) => {
                  // iter194 — cursor-jump fix. NO DOM value mutation here:
                  // the old .toUpperCase() + setSelectionRange dance reset
                  // the caret to end-of-line on iPad/Safari. Display is
                  // uppercased purely via the CSS `uppercase` class and the
                  // text is uppercased once on save (readFindingText).
                  const v = e.target.value;
                  findingTextRef.current = v;
                  // Only flip the boolean (= one re-render) on empty/non-empty
                  // transitions. Re-renders during continuous typing stay 0.
                  const nowHas = v.trim().length > 0;
                  if (nowHas !== hasFindingText) setHasFindingText(nowHas);
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    if (!saving) addFinding();
                  }
                }}
                placeholder="E.G. LEFT REAR SHOCK LEAKING (TAP ANYWHERE — ⌘/CTRL+ENTER TO SAVE)"
                rows={2}
                className="text-base touch-manipulation min-h-[72px] resize-y leading-snug uppercase"
                data-testid="finding-editor-text"
              />
            </div>
            <div className="col-span-3 md:col-span-1">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Qty</label>
              <Input
                type="number" inputMode="decimal" min="0" step="0.5"
                value={draft.quantity}
                onChange={(e) => setDraft(d => ({ ...d, quantity: e.target.value }))}
                className="h-11 text-base text-right touch-manipulation"
                data-testid="finding-editor-qty"
              />
            </div>
            <div className="col-span-3 md:col-span-1">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Hrs</label>
              <Input
                type="number" inputMode="decimal" min="0" step="0.25"
                value={draft.estimated_hours}
                onChange={(e) => setDraft(d => ({ ...d, estimated_hours: e.target.value }))}
                placeholder="est"
                className="h-11 text-base text-right touch-manipulation"
                data-testid="finding-editor-hours"
              />
            </div>
            <div className="col-span-6 md:col-span-2">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Severity</label>
              <Select value={draft.severity} onValueChange={(v) => setDraft(d => ({ ...d, severity: v }))}>
                <SelectTrigger className="h-11 text-sm touch-manipulation" data-testid="finding-editor-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* iter138.5 — Supplier + ORP moved INLINE (was a separate
                row below). Visible only to SM/FM/SA — mechanic role
                NEVER sees ORP. */}
            {isServiceManager && (
              <>
                <div className="col-span-8 md:col-span-2">
                  <label className="text-[10px] uppercase tracking-wide text-emerald-700">Supplier</label>
                  <SupplierCombobox
                    value={draft.parts_supplier || ''}
                    onChange={(v) => setDraft(d => ({ ...d, parts_supplier: v }))}
                    placeholder="Repco…"
                    className="bg-white"
                    testId="finding-editor-supplier"
                    size="md"
                  />
                </div>
                <div className="col-span-4 md:col-span-1">
                  <label className="text-[10px] uppercase tracking-wide text-emerald-700">ORP $</label>
                  <Input
                    type="number" inputMode="decimal" min="0" step="0.01"
                    value={draft.parts_cost_price}
                    onChange={(e) => setDraft(d => ({ ...d, parts_cost_price: e.target.value }))}
                    placeholder="cost"
                    className="h-11 text-sm text-right bg-white touch-manipulation"
                    data-testid="finding-editor-orp"
                  />
                </div>
              </>
            )}
            <div className="col-span-6 md:col-span-1">
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="h-11 w-full text-sm touch-manipulation"
                title="Take or upload photo (attaches when you click +)"
                data-testid="finding-editor-camera"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            <div className="col-span-6 md:col-span-1">
              <Button
                size="sm"
                onClick={addFinding}
                disabled={saving || !hasFindingText}
                className="h-11 w-full text-sm bg-amber-600 hover:bg-amber-700 touch-manipulation"
                data-testid="finding-editor-add"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* iter138.5 — Old SM-only second row (Supplier + ORP) removed.
              Both fields now live INLINE on the primary editor row above. */}

          {/* Pending photos preview — staged client-side until the row is saved.
              iter138.5 — Clicking a thumbnail now opens it full-size in the
              shared photo viewer dialog so the mechanic can verify the
              photo is in-focus / shows the right detail BEFORE saving. */}
          {pendingPhotos.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="finding-editor-pending-photos">
              <span className="text-[10px] text-slate-500">Will attach (tap to enlarge):</span>
              {pendingPhotos.map((p, i) => (
                <div key={i} className="relative">
                  <button
                    type="button"
                    onClick={() => setPendingPreviewIdx(i)}
                    className="h-12 w-12 rounded border border-slate-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-400"
                    title="Tap to enlarge"
                    data-testid={`finding-pending-photo-${i}`}
                  >
                    <img src={p.data} alt={p.name} className="h-full w-full object-cover" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingPhotos(prev => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full p-0.5"
                    title="Remove"
                    data-testid={`finding-pending-photo-remove-${i}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* hidden file inputs for camera capture */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleDraftPhoto}
            data-testid="finding-editor-file"
          />
          <input
            ref={rowFileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleRowPhoto}
          />
        </div>
      )}

      {!canEdit && items.length === 0 && (
        <p className="text-xs text-slate-400 italic">No structured findings yet.</p>
      )}

      {/* iter138.5 — Pending photo preview dialog. Lets the mechanic
          tap a thumbnail to verify the photo BEFORE saving the finding
          (previously the thumbnail was just static and they had no way
          to check focus / framing on the iPad). */}
      <Dialog
        open={pendingPreviewIdx !== null}
        onOpenChange={(o) => !o && setPendingPreviewIdx(null)}
      >
        <DialogContent className="!max-w-[80vw] max-h-[88vh] overflow-y-auto p-6" data-testid="finding-pending-preview">
          <DialogHeader>
            <DialogTitle className="text-base">
              Photo preview · not yet saved
              <span className="ml-2 text-xs font-normal text-slate-500">
                Tap × to remove, or click the + to save this finding with the photo.
              </span>
            </DialogTitle>
          </DialogHeader>
          {pendingPreviewIdx !== null && pendingPhotos[pendingPreviewIdx] && (
            <div className="space-y-2 mt-2">
              <img
                src={pendingPhotos[pendingPreviewIdx].data}
                alt={pendingPhotos[pendingPreviewIdx].name}
                className="w-full max-h-[70vh] object-contain rounded border border-slate-200 bg-slate-50 mx-auto"
              />
              <div className="text-xs text-slate-500 truncate">
                {pendingPhotos[pendingPreviewIdx].name}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo viewer — single column, one image below another */}
      <Dialog open={!!viewerFinding} onOpenChange={(o) => !o && setViewerFinding(null)}>
        <DialogContent className="!max-w-[80vw] max-h-[88vh] overflow-y-auto p-6">
          <DialogHeader>
            <DialogTitle className="text-base">
              Photos · {viewerFinding?.text}
              <span className="ml-2 text-xs font-normal text-slate-500">
                {(viewerFinding?.photos || []).length} image(s)
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">Photos attached to this mechanic finding.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2" data-testid="finding-photo-viewer">
            {(viewerFinding?.photos || []).length === 0 && (
              <p className="text-sm text-slate-500 italic">No photos for this finding.</p>
            )}
            {(viewerFinding?.photos || []).map((p) => (
              <div key={p.photo_id} className="relative rounded border border-slate-200 overflow-hidden bg-slate-50">
                <AuthImage
                  src={p.url}
                  alt={p.filename}
                  className="w-full max-h-[70vh] object-contain mx-auto"
                  testId={`finding-photo-img-${p.photo_id}`}
                />
                <div className="flex items-center justify-between bg-slate-900/70 text-white px-3 py-1.5 text-xs">
                  <span className="truncate">{p.filename}</span>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => deletePhoto(viewerFinding.finding_id, p.photo_id)}
                      className="text-rose-300 hover:text-rose-500"
                      data-testid={`finding-photo-delete-${p.photo_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
